import "zod/compile";
import { z } from "zod";

/**
 * The Discounts v2 metric catalog for the agent harness. "Created" counts
 * elements (one element = one discount) and groups (the batch a discount
 * arrived in — a single exception price list can mint hundreds of elements).
 * "Applied" means a live draft carries it; "used" means an order consumed it.
 */
export const DISCOUNT_MEASURES = {
  groups_created:
    "Discount groups created in the window (a group is a request / batch; status in group_status).",
  elements_created:
    "Discount elements created in the window (one element = one discount line; report alongside groups_created).",
  groups_approved:
    "Discount groups approved in the window (from the approval activity; falls back to the group's last update when no activity was recorded).",
  elements_used:
    "Elements an order has consumed (used quantity > 0 or single-use consumed). Covers both submission paths. Windowed on the element's creation date, not the order date.",
  elements_applied_on_drafts:
    "Distinct elements currently applied on live draft stacks (not yet submitted, not rejected, not superseded).",
  drafts_with_discounts:
    "Live drafts (not submitted / rejected / superseded) that carry at least one discount stack.",
  applied_value:
    "Sum of live draft stacks' discount amounts — (list price − discounted price) × quantity — per currency. Pipeline, not yet realized.",
  realized_value:
    "Sum of discount amounts frozen on submitted orders (adapter path plus approve-only submissions), excluding reversed lines, per currency.",
} as const;
export const DiscountMeasureSchema = z.enum(
  Object.keys(DISCOUNT_MEASURES) as [
    keyof typeof DISCOUNT_MEASURES,
    ...Array<keyof typeof DISCOUNT_MEASURES>,
  ],
);
export type DiscountMeasure = z.infer<typeof DiscountMeasureSchema>;

export const DISCOUNT_DATE_BASES = {
  created_at: "When the group / element was created (creation measures).",
  approved_at:
    "When the group was approved (groups_approved); other measures fall back to created_at.",
  order_date:
    "The submitted order's order date (realized_value only); other measures fall back to created_at.",
  submitted_at:
    "When the order was submitted (realized_value); other measures fall back to created_at.",
} as const;
export const DiscountDateBasisSchema = z.enum(
  Object.keys(DISCOUNT_DATE_BASES) as [
    keyof typeof DISCOUNT_DATE_BASES,
    ...Array<keyof typeof DISCOUNT_DATE_BASES>,
  ],
);
export type DiscountDateBasis = z.infer<typeof DiscountDateBasisSchema>;

export const DISCOUNT_GROUP_BYS = {
  customer:
    "One row per customer the group / element is scoped to (blank = all customers).",
  discount_type:
    "One row per element type: PERCENT_BPS_OFF, AMOUNT_OFF, FIXED_PRICE.",
  group_status:
    "One row per group status: DRAFT, PENDING_APPROVAL, APPROVED, REJECTED.",
} as const;
export const DiscountGroupBySchema = z.enum(
  Object.keys(DISCOUNT_GROUP_BYS) as [
    keyof typeof DISCOUNT_GROUP_BYS,
    ...Array<keyof typeof DISCOUNT_GROUP_BYS>,
  ],
);
export type DiscountGroupBy = z.infer<typeof DiscountGroupBySchema>;

export const DiscountMetricRowSchema = z.object({
  groupKey: z.string().nullable(),
  groupLabel: z.string().nullable(),
  /** Set for value measures; null for counts. */
  currency: z.string().nullable(),
  value: z.number(),
});
export type DiscountMetricRow = z.infer<typeof DiscountMetricRowSchema>;

export const DiscountMetricCompanionsSchema = z.object({
  /** Elements with no group (legacy / ad hoc); included only for unrestricted users. */
  elementsWithoutGroup: z.number().int(),
  /** APPROVED groups with no approval activity row (approved_at fell back to updated_at). */
  approvedWithoutActivity: z.number().int(),
  /** Realized value coming from approve-only submissions (frozen snapshot, no sales order row). */
  approveOnlyRealizedCount: z.number().int(),
});
export type DiscountMetricCompanions = z.infer<
  typeof DiscountMetricCompanionsSchema
>;
